/**
 * scheduleMachines.ts
 * Frontend service for the Machines API (Phase 1, Item 5).
 *
 * All requests go through apiClient (MSAL auth + error capture).
 * Endpoints:
 *   GET    /api/machines           — list with capabilities
 *   GET    /api/machines/:key      — single with capabilities
 *   POST   /api/machines           — create
 *   PUT    /api/machines/:key      — update (incl. TaskTypes)
 *   DELETE /api/machines/:key      — soft delete
 *
 * Phase 5.2: TaskTypeCapabilities carries per-pairing MachineMinutes.
 */

import apiClient from './apiClient';
import type { MachineStatus, LabSection } from '../types/schedule';

// ── Types ────────────────────────────────────────────────────────────────────

export interface MachineTaskTypeRef {
  TaskTypeKey: string;
  Name: string;
  Category: string | null;
  /**
   * Run Mode Phase A: the task type's run mode — drives the per-mode minutes
   * helper text in MachineFormDialog ('attended' packs into 8h working days;
   * 'continuous' is total 24/7 elapsed time). Optional: older payload shapes
   * omit it; treat missing as 'attended'.
   */
  RunMode?: 'attended' | 'continuous';
  /** Phase 5.2 / D1: machine time in minutes for this test×machine pairing. */
  MachineMinutes: number | null;
}

/** Phase 5.2: per-pairing capability descriptor (key + minutes). */
export interface TaskTypeCapabilityInput {
  TaskTypeKey: string;
  /** Machine time in minutes for this pairing. Null = unmapped. */
  MachineMinutes?: number | null;
}

export interface MachineRow {
  MachineKey: string;
  Code: string;
  Name: string;
  LabSection: LabSection | null;
  /** Migration 120: second-level grouping — the test family this machine serves (admin-editable). */
  MachineGroup: string | null;
  Status: MachineStatus;
  Notes: string | null;
  IsActive: boolean;
  TaskTypes: MachineTaskTypeRef[];
}

/**
 * REQ-06 (round 2): a test this machine supports, with whether THIS machine is
 * the test's per-test default (TestCatalogItem.DefaultMachineKey === this machine).
 * Returned by GET /api/machines/:key/supported-tests, ordered by Category then
 * TestName. GUIDs come back as SQL Server returns them (typically upper-cased) —
 * compare case-insensitively.
 */
export interface MachineSupportedTest {
  TestCatalogItemKey: string;
  TestName: string;
  AstmCode: string | null;
  Category: string | null;
  CurrentlyDefault: boolean;
}

export interface CreateMachinePayload {
  Code: string;
  Name: string;
  LabSection?: LabSection;
  MachineGroup?: string | null;
  Status?: MachineStatus;
  Notes?: string;
  /** Legacy: keys only (no minutes). Use TaskTypeCapabilities for Phase 5.2. */
  TaskTypeKeys?: string[];
  /** Phase 5.2: per-pairing list with minutes. Takes precedence over TaskTypeKeys. */
  TaskTypeCapabilities?: TaskTypeCapabilityInput[];
}

export interface UpdateMachinePayload {
  Code?: string;
  Name?: string;
  LabSection?: LabSection | null;
  MachineGroup?: string | null;
  Status?: MachineStatus;
  Notes?: string | null;
  /** Legacy: keys only (no minutes). Use TaskTypeCapabilities for Phase 5.2. */
  TaskTypeKeys?: string[];
  /** Phase 5.2: per-pairing list with minutes. Takes precedence over TaskTypeKeys. */
  TaskTypeCapabilities?: TaskTypeCapabilityInput[];
}

// ── Service functions ─────────────────────────────────────────────────────────

export async function fetchMachines(): Promise<MachineRow[]> {
  const response = await apiClient.get<MachineRow[]>('/machines');
  return response.data;
}

export async function fetchMachine(key: string): Promise<MachineRow> {
  const response = await apiClient.get<MachineRow>(`/machines/${key}`);
  return response.data;
}

/**
 * REQ-06 (round 2): GET /api/machines/:key/supported-tests
 * Returns the active tests this machine is capable of (union across the test's
 * task types), each flagged with CurrentlyDefault (= this machine is the test's
 * per-test default). Read-only — to change a default, call updateTestCatalogItem
 * with { DefaultMachineKey }. Returns [] when the machine supports no active tests.
 */
export async function fetchMachineSupportedTests(
  machineKey: string,
): Promise<MachineSupportedTest[]> {
  const response = await apiClient.get<MachineSupportedTest[]>(
    `/machines/${machineKey}/supported-tests`,
  );
  return response.data;
}

export async function createMachine(data: CreateMachinePayload): Promise<MachineRow> {
  const response = await apiClient.post<MachineRow>('/machines', data);
  return response.data;
}

export async function updateMachine(key: string, data: UpdateMachinePayload): Promise<MachineRow> {
  const response = await apiClient.put<MachineRow>(`/machines/${key}`, data);
  return response.data;
}

export async function deleteMachine(key: string): Promise<void> {
  await apiClient.delete(`/machines/${key}`);
}

/**
 * G2-10: one labelled future booking from the deactivate 409 needsConfirm
 * payload (FUTURE_BOOKINGS_CONFIRM).
 */
export interface DeactivateFutureBooking {
  MachineBookingKey: string;
  StartDate: string;
  EndDate: string;
  Status: string;
  EventType: string | null;
  TaskName: string | null;
  QuoteNumber: string | null;
  WorkOrderId: string | null;
}

/**
 * G2-10: thrown by deactivateMachine when the machine still has active FUTURE
 * bookings and confirm was not set — carries the count + labelled bookings so
 * the page can render the relocation prompt (advisory, never blocks).
 */
export class DeactivateNeedsConfirmError extends Error {
  readonly needsConfirm = true;
  readonly futureBookingCount: number;
  readonly futureBookings: DeactivateFutureBooking[];
  constructor(message: string, count: number, bookings: DeactivateFutureBooking[]) {
    super(message);
    this.name = 'DeactivateNeedsConfirmError';
    this.futureBookingCount = count;
    this.futureBookings = bookings;
  }
}

/**
 * Phase 5.7: Deactivate (soft-retire) a machine without deleting it.
 * Sets Status to 'Decommissioned' (or 'OutOfService') and IsActive=false.
 * Bookings are preserved; machine disappears from new pickers.
 *
 * G2-10 (audit #2): two-phase confirm — when the machine has active FUTURE
 * bookings and `confirm` is false, the API answers 409 needsConfirm
 * (FUTURE_BOOKINGS_CONFIRM), thrown here as DeactivateNeedsConfirmError with
 * the booking list. Resend with confirm=true to proceed (bookings are kept).
 */
export async function deactivateMachine(
  key: string,
  status: 'Decommissioned' | 'OutOfService' = 'Decommissioned',
  confirm = false,
): Promise<MachineRow> {
  try {
    const response = await apiClient.post<MachineRow>(
      `/machines/${key}/deactivate`,
      { status, confirm },
    );
    return response.data;
  } catch (err: unknown) {
    const axiosErr = err as {
      response?: {
        status?: number;
        data?: {
          error?: string;
          needsConfirm?: boolean;
          futureBookingCount?: number;
          futureBookings?: DeactivateFutureBooking[];
        };
      };
    };
    const data = axiosErr?.response?.data;
    if (axiosErr?.response?.status === 409 && data?.needsConfirm === true) {
      throw new DeactivateNeedsConfirmError(
        data.error ?? 'This machine has future bookings.',
        data.futureBookingCount ?? data.futureBookings?.length ?? 0,
        data.futureBookings ?? [],
      );
    }
    throw err;
  }
}
